import { inject, injectable } from 'tsyringe';
import { AuthFetchToken, type AuthFetch } from '@/container/IntegrationTokens';

/** BFF proxy prefix for auth-api (stripped by the upstream router → :3001). */
const GRID_VIEWS_BASE = '/_auth/api/me/grid-views';

/** How long to coalesce rapid saves (e.g. per-keystroke search changes) before flushing to the server. */
const SAVE_DEBOUNCE_MS = 800;

/**
 * Persists a grid's saved-views blob per user (server-side, cross-device) via
 * auth-api. The payload is opaque JSON owned by the MegaGrid component — this
 * service only transports it. Writes are debounced per grid key and
 * best-effort: a failed flush leaves the browser's localStorage cache as the
 * durable copy, so the grid never blocks on the network.
 */
@injectable()
export class GridViewsService {
    private readonly pending = new Map<string, ReturnType<typeof setTimeout>>();

    constructor(@inject(AuthFetchToken) private readonly authFetch: AuthFetch) {}

    async load(gridKey: string): Promise<Record<string, unknown> | null> {
        try {
            const response = await this.authFetch(`${GRID_VIEWS_BASE}/${encodeURIComponent(gridKey)}`);
            if (!response.ok) return null;
            const body: unknown = await response.json();
            return body !== null && typeof body === 'object' && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
        } catch {
            return null;
        }
    }

    /** Debounced, fire-and-forget write-through. The latest payload per key wins. */
    save(gridKey: string, payload: Record<string, unknown>): void {
        const existing = this.pending.get(gridKey);
        if (existing) clearTimeout(existing);
        this.pending.set(
            gridKey,
            setTimeout(() => {
                this.pending.delete(gridKey);
                void this.flush(gridKey, payload);
            }, SAVE_DEBOUNCE_MS),
        );
    }

    private async flush(gridKey: string, payload: Record<string, unknown>): Promise<void> {
        try {
            await this.authFetch(`${GRID_VIEWS_BASE}/${encodeURIComponent(gridKey)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
        } catch {
            void 0;
        }
    }
}
